import { ArticlesType } from "@faire/help-center-utils/hooks/article/useArticles";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import { route as confirmIdentityRoute } from "@faire/web-api--source/routes/confirm-identity";
import {
  path as netTermsPath,
  route as netTermsRoute,
} from "@faire/web-api--source/routes/net-terms";
import { route as posRoute } from "@faire/web-api--source/routes/point-of-sale";
import { route as availableCreditRoute } from "@faire/web-api--source/routes/retailer/settings/available-credit";
import { route as emailPreferencesRoute } from "@faire/web-api--source/routes/retailer/settings/email-preferences";
import { route as insiderSettingsRoute } from "@faire/web-api--source/routes/retailer/settings/insider-membership";
import { route as myAddressesRoute } from "@faire/web-api--source/routes/retailer/settings/my-addresses";
import { route as myTeamRoute } from "@faire/web-api--source/routes/retailer/settings/my-team";
import {
  path as paymentMethodsPath,
  route as paymentMethodsRoute,
} from "@faire/web-api--source/routes/retailer/settings/payment-methods";
import { route as myAccountRoute } from "@faire/web-api--source/routes/retailer/settings/profile";
import home from "@faire/web-api--source/routes/support";
import articlePage from "@faire/web-api--source/routes/support/articles/articleId";
import { path as orderInvoicePath } from "@faire/web-api--source/routes/user/account/invoices/invoiceToken";
import { route as paidInvoicesRoute } from "@faire/web-api--source/routes/user/account/invoices/paid";
import { route as unpaidInvoicesRoute } from "@faire/web-api--source/routes/user/account/invoices/unpaid";
import {
  path as getOrdersRoute,
  QueryParameters as orderRouteQueryParameters,
} from "@faire/web-api--source/routes/user/account/orders";
import {
  path as getOrderDetailsRoute,
  QueryParameters as orderDetailsRouteQueryParameters,
} from "@faire/web-api--source/routes/user/account/orders/orderToken/brandToken";
import { path as getReportOrderItemRoute } from "@faire/web-api--source/routes/user/account/orders/orderToken/brandToken/report";
import {
  QueryParameters as createReturnRouteQueryParameters,
  path as getCreateReturnRoute,
} from "@faire/web-api--source/routes/user/account/orders/orderToken/brandToken/return";
import { route as brandReferralsRoute } from "@faire/web-api--source/routes/user/brand-referrals";
import { route as insiderInfoRoute } from "@faire/web-api--source/routes/user/insider/info";

import { InstantAnswerCTA } from "../../types";

export const CheckShipmentStatusCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Check shipment status",
    description: {
      text: "CTA button to track order shipment status",
    },
  }),
  getLink: getLinkForOrderShipmentStatusPage,
});

export const ReportItemsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Report item(s)",
    description: {
      text: "CTA button to report damaged, missing, or incorrect item(s)",
    },
  }),
  getLink: getLinkForOrderItemReportModal,
});

export const RequestExtensionCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Request an extension",
    description: {
      text: "CTA button to request an extension for reporting damaged, missing, or incorrect items",
    },
  }),
  getLink: getLinkForRequestReportExtensionModal,
});

export const GoToOrdersCTA = (strictLocalize: StrictLocalizeFunction) => {
  return {
    label: strictLocalize({
      defaultMessage: "Go to Orders",
      description: {
        text: "CTA button to go to the brand orders page",
      },
    }),
    getLink: () => getOrdersRoute(),
  };
};

export const CreateReturnCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Create a return",
    description: {
      text: "CTA button to create a return report for the selected order",
    },
  }),
  getLink: getLinkForCreateReturnModal,
});

export const ExtendReturnDateCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Extend return date",
    description: {
      text: "CTA button to extend the return date for the selected order",
    },
  }),
  getLink: getLinkForRequestReturnWindowExtensionModal,
});

export const RequestDutiesRefundCTA = (
  strictLocalize: StrictLocalizeFunction,
  articles?: ArticlesType
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Request reimbursement",
    description: {
      text: "CTA button to request a refund of duties",
    },
  }),
  getLink: (_?: ISelfServeBrandOrder) => {
    return articles?.ImportFeesAndDuties.DUTIES_REFUND
      ? articles.ImportFeesAndDuties.DUTIES_REFUND?.atSection("1").getLink()
      : undefined;
  },
});

export const GoToOrderDetailsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Go to Order Details",
    description: {
      text: "CTA button to go to order details page of the selected order",
    },
  }),
  getLink: getLinkForOrderDetailsPage,
});

export const ManageShippingAddressesCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Manage shipping addresses",
    description: {
      text: "CTA button to the retailer's my addresses page",
    },
  }),
  getLink: () => myAddressesRoute,
});

export const GoToAccountSettingsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Go to Account Settings",
    description: {
      text: "CTA button to message the brand from whom the retailer purchased the order",
    },
  }),
  getLink: () => myAccountRoute,
});

export const VerifyAccountCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Verify account",
    description: {
      text: "CTA button to verify the retailer's account",
    },
  }),
  getLink: () => confirmIdentityRoute,
});

export const ManageMyTeamCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Manage my team",
    description: {
      text: "CTA button to go to the My Team page",
    },
  }),
  getLink: () => myTeamRoute,
});

export const UpdateEmailPreferencesCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Update email preferences",
    description: {
      text: "CTA button to go to the Email Preferences page",
    },
  }),
  getLink: () => emailPreferencesRoute,
});

export const ApplyForEoriCTA = (
  strictLocalize: StrictLocalizeFunction,
  articles?: ArticlesType
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Apply for EORI",
    description: {
      text: "CTA button to the Help Center artile containing country specific links to apply for EORI number ",
    },
  }),
  getLink: () =>
    articles?.ImportFeesAndDuties.EORI
      ? articlePage(articles.ImportFeesAndDuties.EORI.id)
      : undefined,
});

export const GoToFairePosCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Go to Faire POS",
    description: {
      text: "CTA button to the Faire POS page",
    },
  }),
  getLink: () => posRoute,
});

export const ViewOrderInvoiceCTA = (
  strictLocalize: StrictLocalizeFunction,
  brandOrder: ISelfServeBrandOrder
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "View order invoice",
    description: {
      text: "CTA button to access an order invoice page",
    },
  }),
  getLink: () => getLinkForOrderInvoice(brandOrder),
});

export const ViewInvoicesCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "View invoices",
    description: {
      text: "CTA button to access the page with unpaid invoices",
    },
  }),
  getLink: () => unpaidInvoicesRoute,
});

export const ViewPaidInvoicesCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "View paid invoices",
    description: {
      text: "CTA button to access the page with paid invoices",
    },
  }),
  getLink: () => paidInvoicesRoute,
});

export const ViewNetTermsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "View terms",
    description: {
      text: "CTA button to go to Net60 terms page",
    },
  }),
  getLink: () => netTermsRoute,
});

export const RequestCreditLimitIncreaseCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Request credit limit increase",
    description: {
      text: "CTA button to go to Net60 terms page to request a credit limit increase",
    },
  }),
  getLink: () => netTermsPath({ limitIncrease: "1" }),
});

export const AddPaymentMethodCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Add payment method",
    description: {
      text: "CTA button to add a payment method",
    },
  }),
  getLink: () => paymentMethodsPath({ addModal: true }),
});

export const ViewOrUpdatePaymentMethodsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "View or update payment methods",
    description: {
      text: "CTA button to the page to view or update payment methods",
    },
  }),
  getLink: () => paymentMethodsRoute,
});

export const GoToBrandReferralsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Go to Brand Referrals",
    description: {
      text: "CTA button to the brand referrals page",
    },
  }),
  getLink: () => brandReferralsRoute,
});

export const CheckAvailableCreditCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Check available credit",
    description: {
      text: "CTA button to the page to check your available credit",
    },
  }),
  getLink: () => availableCreditRoute,
});

export const ViewOfferDetailsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "View offer details",
    description: {
      text: "CTA button to the page to check your available credit and view offer details",
    },
  }),
  getLink: () => availableCreditRoute,
});

export const InsiderBenefitsLearnMoreCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Learn more",
    description: {
      text: "CTA button to the page to learn more about Insider benefits",
    },
  }),
  getLink: () => insiderInfoRoute,
});

export const GoToMembershipSettingsCTA = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerCTA => ({
  label: strictLocalize({
    defaultMessage: "Go to Membership Settings",
    description: {
      text: "CTA button to the membership settings page",
    },
  }),
  getLink: () => insiderSettingsRoute,
});

const getLinkForOrderItemReportModal = (brandOrder?: ISelfServeBrandOrder) => {
  const selectedBrandOrder = brandOrder?.brand_order;
  const orderToken = selectedBrandOrder?.order_token;
  const orderNumber = brandOrder?.order_number;
  const brandToken = selectedBrandOrder?.brand_token;
  if ((!orderToken && !orderNumber) || !brandToken) {
    return home();
  }

  const orderId = orderToken ?? `o_${orderNumber?.toLowerCase()}`;

  return getReportOrderItemRoute(orderId, brandToken);
};

const getLinkForOrderShipmentStatusPage = (
  brandOrder?: ISelfServeBrandOrder
) => {
  const orderNumber = brandOrder?.order_number;

  return orderNumber
    ? getOrdersRoute(orderRouteQueryParameters.build({ query: orderNumber }))
    : home();
};

const getLinkForRequestReportExtensionModal = () =>
  getOrdersRoute(
    orderRouteQueryParameters.build({ action: "request-report-issue" })
  );

const getLinkForCreateReturnModal = (brandOrder?: ISelfServeBrandOrder) => {
  const selectedBrandOrder = brandOrder?.brand_order;
  const orderToken = selectedBrandOrder?.order_token;
  const orderNumber = brandOrder?.order_number;
  const brandToken = selectedBrandOrder?.brand_token;
  const brandOrderToken = selectedBrandOrder?.token;
  if ((!orderToken && !orderNumber) || !brandToken || !brandOrderToken) {
    return home();
  }

  const orderId = orderToken ?? `o_${orderNumber?.toLowerCase()}`;

  return getCreateReturnRoute(
    orderId,
    brandToken,
    createReturnRouteQueryParameters.build({ brandOrderToken })
  );
};

const getLinkForRequestReturnWindowExtensionModal = () =>
  getOrdersRoute(
    orderRouteQueryParameters.build({ action: "extend-cancel-window" })
  );

const getLinkForOrderDetailsPage = (brandOrder?: ISelfServeBrandOrder) => {
  const selectedBrandOrder = brandOrder?.brand_order;
  const orderToken = selectedBrandOrder?.order_token;
  const orderNumber = brandOrder?.order_number;
  const brandToken = selectedBrandOrder?.brand_token;
  const brandOrderToken = selectedBrandOrder?.token;
  if ((!orderToken && !orderNumber) || !brandToken || !brandOrderToken) {
    return home();
  }

  const orderId = orderToken ?? `o_${orderNumber?.toLowerCase()}`;

  return getOrderDetailsRoute(
    orderId,
    brandToken,
    orderDetailsRouteQueryParameters.build({ brandOrderToken })
  );
};

const getLinkForOrderInvoice = (brandOrder?: ISelfServeBrandOrder) => {
  const orderToken = brandOrder?.brand_order?.token;
  if (!orderToken) {
    return unpaidInvoicesRoute;
  }

  return orderInvoicePath(orderToken);
};
